import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { img } from '../data/siteData';
import useIsMobile from '../tools/UseIsMobile';
import GalleryThumbnails from './GalleryThumbnails';

function PagePhotoViewer({ title, images, leadText, eyebrow = 'Photo Detail', showLeadCopy = true, layout = 'auto' }) {
  const isMobile = useIsMobile();
  const viewerImages = images?.length ? images : [];
  const [displayImageIndex, setDisplayImageIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [zoom, setZoom] = useState(1);
  const displayTitle = title || 'C&M equipment';
  const primaryImage = viewerImages[displayImageIndex] || viewerImages[0];
  const secondaryImages = viewerImages;
  const modalImage = activeImageIndex == null ? null : viewerImages[activeImageIndex];
  const hasCopy = showLeadCopy && Boolean(title || leadText);
  const isStacked = !hasCopy && layout !== 'split';
  const clampZoom = (value) => Math.min(3, Math.max(1, Number(value.toFixed(2))));

  const closeImage = () => {
    setActiveImageIndex(null);
    setZoom(1);
  };

  const openImage = (imagePath) => {
    setActiveImageIndex(Math.max(0, viewerImages.indexOf(imagePath)));
    setZoom(1);
  };

  const selectImage = (index) => {
    setDisplayImageIndex(index);
  };

  const changeZoom = (amount) => setZoom((value) => clampZoom(value + amount));

  const stepImage = (direction) => {
    setActiveImageIndex((index) => {
      if (index == null || viewerImages.length === 0) return index;
      return (index + direction + viewerImages.length) % viewerImages.length;
    });
    setZoom(1);
  };

  const handleWheelZoom = (event) => {
    event.preventDefault();
    event.stopPropagation();
    changeZoom(event.deltaY > 0 ? -0.15 : 0.15);
  };

  useEffect(() => {
    if (!modalImage) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [modalImage]);

  useEffect(() => {
    setDisplayImageIndex(0);
    setActiveImageIndex(null);
    setZoom(1);
  }, [images]);

  if (!primaryImage) return null;

  const thumbnails = (
    <GalleryThumbnails images={secondaryImages} activeIndex={displayImageIndex} onSelect={selectImage} title={displayTitle} naturalRatio={isStacked} />
  );

  return (
    <>
      <section className={`page-visual-lead ${hasCopy ? 'has-copy' : 'media-only'} ${isMobile ? 'is-mobile-viewer' : 'is-desktop-viewer'}${isStacked ? ' stacked-gallery' : ''}`}>
        <div className="page-visual-media">
          <figure>
            <button type="button" onClick={() => openImage(primaryImage)} aria-label={`Expand ${displayTitle} photo`}>
              <img src={img(primaryImage)} alt={`${displayTitle} main equipment photo`} />
              <span><Maximize2 size={15} /> Expand</span>
            </button>
          </figure>
          {secondaryImages.length > 0 && hasCopy && !isMobile ? thumbnails : null}
        </div>
        <div className="page-visual-copy">
          {hasCopy ? (
            <>
              <span className="eyebrow">{eyebrow}</span>
              {title ? <h3>{title}</h3> : null}
              <div className="body-copy">{leadText}</div>
            </>
          ) : null}
          {secondaryImages.length > 0 && (!hasCopy || isMobile) ? thumbnails : null}
        </div>
      </section>
      {modalImage ? (
        <div className="image-modal" role="dialog" aria-modal="true" aria-label={`${displayTitle} photo viewer`} onWheel={handleWheelZoom}>
          <button className="modal-close" type="button" onClick={closeImage} aria-label="Close image viewer"><X size={22} /></button>
          <div className="zoom-controls" aria-label="Image zoom controls">
            <button type="button" onClick={() => changeZoom(-0.25)} aria-label="Zoom out"><Minus size={18} /></button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => changeZoom(0.25)} aria-label="Zoom in"><Plus size={18} /></button>
            <button type="button" onClick={() => setZoom(1)} aria-label="Reset zoom"><RotateCcw size={17} /></button>
          </div>
          {viewerImages.length > 1 ? (
            <button className="modal-step left" type="button" onClick={() => stepImage(-1)} aria-label="Previous photo"><ChevronLeft size={28} /></button>
          ) : null}
          <div className="modal-image-stage">
            <img style={{ transform: `scale(${zoom})` }} src={img(modalImage)} alt={`${displayTitle} expanded photo`} />
          </div>
          {viewerImages.length > 1 ? (
            <button className="modal-step right" type="button" onClick={() => stepImage(1)} aria-label="Next photo"><ChevronRight size={28} /></button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default PagePhotoViewer;
